var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setGlobalSearch } from "@reducers/nodes/slice/nodes-slice";
import { PageContainer, PageContent } from "../../components/wraper";
import { GridFilters } from "../../components/grid/GridFilters";
import PageHeader from "@ui/components/header/PageHeader";
import { StatusIndicator } from "@ui/components/status";
import { SButton } from "@ui/components/buttons/SButton";
import { useAlarmsFilter } from "./hooks/useAlarmsFilter";
import { useTranslation } from "react-i18next";
import { usersApi } from "@api/enpoints/users-api";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { SecondaryGrid } from "@ui/components/grid/SecondaryGrid";
import userService from "@utility/user-service";
import { AlarmCard } from "./components/AlarmCard";
const AlarmsPage = () => {
    var _a, _b;
    const { t } = useTranslation(["form", "validation", "common"]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [activeUsers, setActiveUsers] = useState([]);
    // Use existing custom hooks
    const { alarms, loading, searchTerm, filters, paginationInfo, currentPageNumber, currentPageSize, handleFilterChange, handleSearchChange, handleToggleStatus, handlePageChange, handlePageSizeChange, } = useAlarmsFilter();
    // Fetch active users for the "Acknowledged by" filter
    useEffect(() => {
        const fetchActiveUsers = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                const response = yield usersApi.getActiveUsers();
                if (((_a = response.data) === null || _a === void 0 ? void 0 : _a.isSuccess) && ((_b = response.data) === null || _b === void 0 ? void 0 : _b.data)) {
                    setActiveUsers(response.data.data);
                }
            }
            catch (error) {
                console.error("Error fetching active users:", error);
            }
        });
        fetchActiveUsers();
    }, []);
    // Convert active users to filter options format { value: id, label: name }
    const userFilterOptions = useMemo(() => {
        return activeUsers.map((user) => ({
            value: user.id,
            label: user.name,
        }));
    }, [activeUsers]);
    const handleRowClick = (params) => {
        const nodeUid = params.row.nodeUid || params.row.nodeName || "";
        if (nodeUid) {
            dispatch(setGlobalSearch(nodeUid));
            navigate("/nodes");
        }
    };
    const handleAcknowledgeClick = (alarmId) => {
        navigate(`/alarms/${alarmId}/acknowledge`);
    };
    const handleViewDetailsClick = (alarmId) => {
        navigate(`/alarms/${alarmId}/view`);
    };
    const handleEditAlarmClick = (alarmId) => {
        navigate(`/alarms/${alarmId}/edit`);
    };
    const handleManageAlarmCauses = () => {
        navigate(`/alarms/manage`);
    };
    const canAcknowledgeAllAlarms = PermissionChecker.hasPermission(Permission.ALARMS_ACKNOWLEDGE) ||
        userService.hasRole("Super Admin");
    const columns = [
        {
            field: "location",
            headerName: t("common:grid.location"),
            flex: 1,
            minWidth: 150,
            filterable: false,
        },
        {
            field: "nodeName",
            headerName: t("common:grid.nodeName"),
            flex: 1,
            minWidth: 120,
            filterable: false,
        },
        {
            field: "date",
            headerName: t("common:grid.dateTime"),
            flex: 1.5,
            minWidth: 120,
            filterable: true,
            filterType: "date",
            renderCell: (params) => (React.createElement(Box, { sx: {
                    height: "100%",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.5,
                } },
                React.createElement(Typography, { variant: "body2" }, params.row.date),
                React.createElement(Typography, { variant: "body2", style: { color: "#454F5B" } }, params.row.time))),
        },
        {
            field: "alarmType",
            headerName: t("common:grid.alarmType"),
            flex: 1.5,
            minWidth: 120,
            filterable: true,
            filterType: "select",
            filterOptions: [
                { value: "1", label: t("common:alarmTypes.lowTemperature") },
                { value: "2", label: t("common:alarmTypes.highTemperature") },
                { value: "5", label: t("common:alarmTypes.noNodeCommunication") },
                { value: "6", label: t("common:alarmTypes.noApCommunication") },
            ],
        },
        {
            field: "value",
            headerName: t("common:grid.value"),
            flex: 1,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => (React.createElement(Box, null,
                React.createElement(Typography, { component: "span" }, params.row.value),
                React.createElement(Typography, { variant: "body2", style: { color: "#454F5B" } }))),
        },
        {
            field: "handledBy",
            headerName: t("common:grid.acknowledgedBy"),
            flex: 1.5,
            minWidth: 130,
            filterable: true,
            filterType: "select",
            filterField: "acknowledgedBy",
            filterOptions: userFilterOptions,
        },
        {
            field: "explanation",
            headerName: t("common:grid.explanation"),
            flex: 2,
            minWidth: 130,
            filterable: true,
            filterType: "text",
        },
        {
            field: "handlingStatus",
            headerName: t("common:grid.acknowledgementStatus"),
            flex: 1.2,
            minWidth: 130,
            filterable: true,
            filterType: "select",
            filterOptions: [
                { value: "acknowledged", label: t("common:grid.acknowledged") },
                { value: "unacknowledged", label: t("common:grid.unacknowledged") },
            ],
            renderCell: (params) => (React.createElement(StatusIndicator, { variant: params.row.handlingStatus })),
        },
        {
            field: "actions",
            headerName: t("common:grid.actions"),
            flex: 0.8,
            minWidth: 165,
            type: "actions",
            filterable: false,
            headerAlign: "center",
            renderCell: (params) => {
                const isAcknowledged = params.row.handlingStatus === "acknowledged";
                const canAcknowledge = PermissionChecker.hasPermission(Permission.ALARMS_ACKNOWLEDGE);
                if (isAcknowledged && canAcknowledge) {
                    return (React.createElement(SButton, { variant: "outlined", color: "primary", size: "medium", sx: { width: "100%" }, onClick: (e) => { e.stopPropagation(); handleEditAlarmClick(params.row.id); } }, t("common:actions.viewEdit")));
                }
                return !isAcknowledged && canAcknowledge ? (React.createElement(SButton, { variant: "outlined", color: "primary", size: "medium", sx: { width: "100%" }, onClick: (e) => { e.stopPropagation(); handleAcknowledgeClick(params.row.id); } }, t("common:actions.acknowledge"))) : null;
            },
        },
    ];
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.alarms"), topActions: React.createElement(Box, { sx: { display: "flex", gap: 1 } },
                canAcknowledgeAllAlarms && (React.createElement(SButton, { variant: "outlined", size: "large", onClick: () => navigate("/alarms/acknowledge-all") }, t("common:actions.acknowledgeAll"))),
                React.createElement(SButton, { variant: "contained", size: "large", onClick: handleManageAlarmCauses }, t("common:actions.manageAlarmCauses"))) }),
        React.createElement(PageContent, null,
            React.createElement(GridFilters, { title: `${(_a = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _a !== void 0 ? _a : alarms.length} ${t("common:pages.alarms")}`, loading: loading, columns: columns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange }),
            React.createElement(SecondaryGrid, { rows: loading ? [] : alarms, columns: columns, loading: loading, renderCard: (row) => (React.createElement(AlarmCard, { row: row, canAcknowledge: PermissionChecker.hasPermission(Permission.ALARMS_ACKNOWLEDGE), onAcknowledge: handleAcknowledgeClick, onViewDetails: handleViewDetailsClick, onEdit: handleEditAlarmClick })), 
                // heightVariant="full"
                checkboxSelection: false, 
                // autoHeight
                onRowClick: handleRowClick, sx: { cursor: "pointer" }, paginationMode: "server", rowCount: (_b = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _b !== void 0 ? _b : 0, paginationModel: {
                    page: currentPageNumber - 1,
                    pageSize: currentPageSize,
                }, onPaginationModelChange: (model) => {
                    if (model.pageSize !== currentPageSize) {
                        handlePageSizeChange(model.pageSize);
                    }
                    else if (model.page + 1 !== currentPageNumber) {
                        handlePageChange(model.page + 1);
                    }
                }, pageSizeOptions: [25, 50] }))));
};
export default AlarmsPage;
